import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import InputField from "common/customInputField";
import Button from "react-bootstrap/Button";
import StoreLookup from "common/storeLookup";
import SearchTable from "./searchTable";
import js_config from "utils/config-variables";
import warning from "svg/warning.svg";
import { searchSitesAction } from "store/siteRepo/actionCreator";
import { updateSiteSearchFields, clearSiteSearchFields } from "store/slices/siteRepo";
import { api } from "store/slices/api";
import every from "lodash/every";
import isEmpty from "lodash/isEmpty";
import { toast } from "react-toastify";
import "./style.scss";

const SiteRepo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useSelector((state) => state.siteRepo);
  const { siteId, siteName, accountNumber, accountName, city, phoneNumber } = search;
  const { storeCcn, fcRole } = useSelector((state) => state.loggedInUser);
  const { data: storeInfo = {}, isError } = api.useGetStoreInfoQuery({ storeNumber: storeCcn });
  const [storeData, setStoreData] = useState({});

  useEffect(() => {
    storeInfo && setStoreData(storeInfo?.[0]);
    if (isError) {
      toast.error("There was an error while fetching store information");
    }
  }, [storeInfo]);

  return (
    <>
      <div className="container-fluid search-form-container">
        <div className="search-form-container__title d-flex justify-content-between align-items-start flex-column flex-md-row align-items-md-center">
          <span className="search-form-container__title--text mb-4 mb-md-0">Site Repository</span>
          <div className="d-flex">
            <Button variant="outline-primary" onClick={() => navigate(`/siteRepo/createSite#select-project`)}>
              Create a new site
            </Button>
            {(fcRole === js_config.ROLE_FC_STORE_MGR || fcRole === js_config.ROLE_FC_OPS_MGR) && (
              <Button
                className="m-0"
                variant="outline-primary"
                onClick={() => navigate(`/siteRepo/updateProducts#select-product-tobe-changed`)}
              >
                Bulk Update Products
              </Button>
            )}
          </div>
        </div>
        <div className="search-form-container__fields">
          <div className="d-flex search-form-container__fields__msg">
            <img src={warning} alt="" />
            <div className="search-form-container__fields__msg--text">At least one input is required</div>
          </div>
          <form className="search-form-container__fields__form">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Site ID"
                  id="siteId"
                  placeholder="Enter Site Id"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteId}
                  onChange={(e) =>
                    dispatch(updateSiteSearchFields({ key: "siteId", value: e.target.value.replace(/\D/g, "") }))
                  }
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Site Name"
                  id="siteName"
                  placeholder="Enter a site name"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={siteName}
                  onChange={(e) => dispatch(updateSiteSearchFields({ key: "siteName", value: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Account Number"
                  id="accountNumber"
                  placeholder="Enter an account number"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={accountNumber}
                  onChange={(e) => dispatch(updateSiteSearchFields({ key: "accountNumber", value: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label={"Account Name"}
                  id="accountName"
                  placeholder="Enter an account name"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={accountName}
                  onChange={(e) => dispatch(updateSiteSearchFields({ key: "accountName", value: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="City"
                  id="city"
                  placeholder="Enter City"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={city}
                  onChange={(e) => dispatch(updateSiteSearchFields({ key: "city", value: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Phone Number"
                  id="phoneNumber"
                  placeholder="Enter a phone number"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^(\d|\-|\.|\(|\))]/, "").substring(0, 14);
                    dispatch(updateSiteSearchFields({ key: "phoneNumber", value }));
                  }}
                />
              </div>
              <StoreLookup
                numberFieldChangeCallback={(storeNumber) => {
                  dispatch(updateSiteSearchFields({ key: "storeNumber", value: storeNumber }));
                  setStoreData((previousData) => ({ ...previousData, storeNumber }));
                }}
                nameFieldChangeCallback={(storeName) => {
                  dispatch(updateSiteSearchFields({ key: "storeName", value: storeName }));
                  setStoreData((previousData) => ({ ...previousData, storeName }));
                }}
                onStoreLookupValidation={() => {}}
                debounceValue={1200}
                defaultData={storeData}
                nameFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group"
                numberFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-4 search-form-container__fields__form-group"
              />
            </div>
          </form>
          <div className="d-flex search-form-container__fields__btn">
            <Button
              variant="primary"
              onClick={() => {
                const phoneNumber = search?.phoneNumber?.replace(/\D/g, "").substring(0, 10);
                const { storeNumber, storeName } = storeData;
                dispatch(searchSitesAction({ ...search, storeName, storeNumber, phoneNumber }));
              }}
              disabled={every(
                [siteName, accountNumber, accountName, city, phoneNumber, storeData?.storeNumber, storeData?.storeName],
                isEmpty,
              )}
            >
              Search
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => {
                setStoreData({ storeName: "", storeNumber: "" });
                dispatch(clearSiteSearchFields());
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
      {search.isShowTable && (
        <div className="container-fluid search-form-container p-0 border-0">
          <SearchTable />
        </div>
      )}
    </>
  );
};

export default SiteRepo;
